import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { dateFormat, initStartDate } from '@core';
import { Params } from '@angular/router';
import { format, startOfWeek } from 'date-fns';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { isEqual as _isEqual } from 'lodash';
import { ReportsDisplayType } from '@app/modules/views/reports/enums/reports.enums';
import { ViewByDates } from '@app/modules/views/reports/models/reports.model';
import { ReportTypesEnum } from '@app/core/services/reports/reports.enum';
import { DaysRange } from '@app/core/services/reports/report.model';
import { User } from '@app/core/services/user/user.model';

export interface ReportsPageState {
	activeDisplay: ReportsDisplayType;
	activeWeekTab: ReportTypesEnum;
	startDate: string;
	previousWeek: string;
	selectedDate?: string;
	range: DaysRange[];
	isMyReports: boolean;
	filterForReports?: Params;
	reportsFilterBy?: string;
	viewByDates?: ViewByDates;
	viewAsUser?: User;
}

const initialState: ReportsPageState = {
	activeDisplay: ReportsDisplayType.Week,
	activeWeekTab: ReportTypesEnum.hours,
	previousWeek: format(startOfWeek(new Date()), dateFormat),
	selectedDate: format(new Date(), dateFormat),
	startDate: format(startOfWeek(new Date()), dateFormat),
	range: [],
	isMyReports: true,
	filterForReports: {},
	reportsFilterBy: '',
	viewByDates: {
		params: {},
		searchParams: {}
	}
};

@Injectable({
	providedIn: 'root'
})
export class ReportsStateService {
	private _store: BehaviorSubject<ReportsPageState> = new BehaviorSubject<ReportsPageState>(initialState);
	private _activeTab: ReportTypesEnum = ReportTypesEnum.hours;

	public store$: Observable<ReportsPageState> = this._store
		.asObservable()
		.pipe(distinctUntilChanged((prev: ReportsPageState, curr: ReportsPageState) => _isEqual(prev, curr)));

	public selectedDate$: Observable<string> = this.store$.pipe(
		map(state => state.selectedDate),
		distinctUntilChanged()
	);

	public triggerReportsRefresh$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor() {}

	get storeValue(): ReportsPageState {
		return this._store.getValue();
	}

	set storeValue(value: ReportsPageState) {
		this._store.next(value);
	}

	get activeTab(): ReportTypesEnum {
		return this._activeTab;
	}

	set activeTab(value: ReportTypesEnum) {
		this._activeTab = value;
	}

	public updateStore(value: Partial<ReportsPageState>): void {
		const currentState: ReportsPageState = this._store.getValue();
		const newState: ReportsPageState = {
			...currentState,
			...value
		};
		this._store.next(newState);
	}

	public updateDisplayState(currentStartDate: Date, activeDisplayState: ReportsDisplayType, selectedDate: Date): void {
		const updatedStartDay: string = initStartDate(currentStartDate, activeDisplayState, selectedDate);
		const updatedValue: ReportsPageState = {
			...this._store.value,
			activeDisplay: activeDisplayState,
			startDate: updatedStartDay,
			selectedDate: format(selectedDate, dateFormat)
		};
		this._store.next(updatedValue);
	}

	public updateSelectedDate(selectedDate: string): void {
		const updatedValue: ReportsPageState = {
			...this._store.value,
			selectedDate: format(new Date(`${selectedDate} 00:00`.replace(/-/g, '/')), dateFormat)
		};
		this._store.next(updatedValue);
	}
}
